import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { fmtTime } from "@/lib/utils";
import { needsDivider, dayDividerLabel } from "@/lib/chat/day-divider";
import { fetchDmRecents, sortByRecent, bumpRecent } from "@/lib/chat/recents";
import { ChatAttachment, MAX_ATTACH_BYTES, formatBytes, pastedImage } from "@/components/ChatAttachment";
import { MessageCircle, X, ChevronLeft, Send, Users, Search } from "lucide-react";

// A floating, movable chat dock so you can read + reply to chats without leaving
// the page you're on. Launcher lives by the bell; clicking a chat notification
// opens the dock straight to that conversation.

export type DockConv = { kind: "dm" | "channel"; id: string; name: string };
type Msg = { id: string; sender_id: string; recipient_id: string | null; channel_id: string | null; content: string | null; is_direct_message: boolean; created_at: string; attachment_path?: string | null; attachment_name?: string | null; attachment_type?: string | null; attachment_size?: number | null };
type Person = { id: string; first_name: string | null; last_name: string | null; email: string | null; profile_picture_path: string | null };
type Channel = { id: string; name: string | null; is_group?: boolean };

const CHAT_CATS = ["dm", "chat_channel", "chat_mention"];

// Map a chat notification link to a conversation key ("dm:<id>" / "channel:<id>").
function convKeyForLink(link?: string | null): string | null {
  if (!link || !link.startsWith("/chat")) return null;
  const p = new URLSearchParams(link.split("?")[1] || "");
  const dm = p.get("dm"), channel = p.get("channel");
  if (dm) return `dm:${dm}`;
  if (channel) return `channel:${channel}`;
  return null;
}

interface Ctx {
  open: boolean;
  setOpen: (o: boolean) => void;
  pending: { conv: DockConv; n: number } | null;
  openConversation: (c: DockConv) => void;
  unread: number;
  refreshUnread: () => void;
}
const ChatDockContext = createContext<Ctx | null>(null);
export function useChatDock() {
  const c = useContext(ChatDockContext);
  if (!c) throw new Error("useChatDock must be used within ChatDockProvider");
  return c;
}

export function ChatDockProvider({ children }: { children: React.ReactNode }) {
  const { employee } = useAuth();
  const [open, setOpenState] = useState(() => localStorage.getItem("chatdock_open") === "1");
  const [pending, setPending] = useState<{ conv: DockConv; n: number } | null>(null);
  const [unread, setUnread] = useState(0);

  const setOpen = useCallback((o: boolean) => { setOpenState(o); localStorage.setItem("chatdock_open", o ? "1" : "0"); }, []);
  const openConversation = useCallback((c: DockConv) => { setPending(p => ({ conv: c, n: (p?.n ?? 0) + 1 })); setOpen(true); }, [setOpen]);

  const refreshUnread = useCallback(async () => {
    if (!employee) return;
    const { count } = await supabase.from("hq_notifications").select("id", { count: "exact", head: true })
      .eq("recipient_id", employee.id).is("read_at", null).in("category", CHAT_CATS);
    setUnread(count ?? 0);
  }, [employee?.id]);

  useEffect(() => { void refreshUnread(); }, [refreshUnread]);
  useEffect(() => {
    if (!employee) return;
    const ch = supabase.channel("chatdock-unread")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "hq_notifications", filter: `recipient_id=eq.${employee.id}` }, () => void refreshUnread())
      .subscribe();
    return () => { void supabase.removeChannel(ch); };
  }, [employee?.id, refreshUnread]);

  return <ChatDockContext.Provider value={{ open, setOpen, pending, openConversation, unread, refreshUnread }}>{children}</ChatDockContext.Provider>;
}

// Header launcher (sits next to the bell).
export function ChatDockLauncher() {
  const { open, setOpen, unread } = useChatDock();
  return (
    <button onClick={() => setOpen(!open)} title="Chats" className="relative rounded-lg p-2 text-gray2 hover:bg-offwhite hover:text-teal-dark">
      <MessageCircle size={20} />
      {unread > 0 && <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-bad px-1 text-[10px] font-bold text-white">{unread > 99 ? "99+" : unread}</span>}
    </button>
  );
}

const DOCK_W = 360, DOCK_H = 520;
function loadPos(): { x: number; y: number } {
  try { const p = JSON.parse(localStorage.getItem("chatdock_pos") || ""); if (typeof p?.x === "number") return clampPos(p.x, p.y); } catch { /* ignore */ }
  return clampPos(window.innerWidth - DOCK_W - 20, window.innerHeight - DOCK_H - 20);
}
function clampPos(x: number, y: number) {
  const maxX = Math.max(8, window.innerWidth - Math.min(DOCK_W, window.innerWidth - 16) - 8);
  const maxY = Math.max(8, window.innerHeight - Math.min(DOCK_H, window.innerHeight - 16) - 8);
  return { x: Math.min(Math.max(8, x), maxX), y: Math.min(Math.max(8, y), maxY) };
}

export function ChatDock() {
  const { open, setOpen, pending, refreshUnread } = useChatDock();
  const { employee } = useAuth();
  const [active, setActive] = useState<DockConv | null>(null);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [peopleMap, setPeopleMap] = useState<Record<string, Person>>({});
  const [messages, setMessages] = useState<Msg[]>([]);
  const [draft, setDraft] = useState("");
  const [sendErr, setSendErr] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [q, setQ] = useState("");
  const [unreadByConv, setUnreadByConv] = useState<Record<string, number>>({});
  // When I last talked to each person, so the direct messages list reads newest conversation first.
  const [recents, setRecents] = useState<Record<string, number>>({});
  const [pos, setPos] = useState(loadPos);
  const scrollRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ dx: number; dy: number } | null>(null);

  // Lists load when the dock opens.
  useEffect(() => {
    if (!open || !employee) return;
    void (async () => {
      const [{ data: chs }, { data: ppl }] = await Promise.all([
        supabase.from("hq_chat_channels").select("id,name,is_group").order("is_group").order("name"),
        supabase.from("hq_directory").select("id,first_name,last_name,email,profile_picture_path").order("first_name"),
      ]);
      setChannels((chs as Channel[]) ?? []);
      const list = ((ppl as Person[]) ?? []);
      setPeople(list.filter(p => p.id !== employee.id));
      setPeopleMap(Object.fromEntries(list.map(p => [p.id, p])));
      setRecents(await fetchDmRecents(employee.id));
    })();
  }, [open, employee?.id]);

  // React to a notification / launcher deep-link request.
  useEffect(() => { if (pending) setActive(pending.conv); }, [pending?.n]);

  // Which conversations have unread messages (from unread chat notifications).
  const refreshUnreadMap = useCallback(async () => {
    if (!employee) return;
    const { data } = await supabase.from("hq_notifications").select("link")
      .eq("recipient_id", employee.id).is("read_at", null).in("category", CHAT_CATS);
    const map: Record<string, number> = {};
    for (const n of (data as { link: string | null }[] ?? [])) { const k = convKeyForLink(n.link); if (k) map[k] = (map[k] ?? 0) + 1; }
    setUnreadByConv(map);
  }, [employee?.id]);

  useEffect(() => {
    if (!open || !employee) return;
    void refreshUnreadMap();
    const ch = supabase.channel("chatdock-unreadmap")
      .on("postgres_changes", { event: "*", schema: "public", table: "hq_notifications", filter: `recipient_id=eq.${employee.id}` }, () => { void refreshUnreadMap(); void fetchDmRecents(employee.id).then(setRecents); })
      .subscribe();
    return () => { void supabase.removeChannel(ch); };
  }, [open, employee?.id, refreshUnreadMap]);

  // Load messages + mark that conversation's chat notifications read.
  useEffect(() => {
    if (!active || !employee) { setMessages([]); return; }
    void loadMessages(active);
  }, [active?.kind, active?.id, employee?.id]);

  async function loadMessages(conv: DockConv) {
    if (!employee) return;
    let query = supabase.from("hq_chat_messages").select("*").is("deleted_at", null).order("created_at", { ascending: false }).limit(50);
    if (conv.kind === "channel") query = query.eq("channel_id", conv.id).eq("is_direct_message", false);
    else query = query.eq("is_direct_message", true).or(`and(sender_id.eq.${employee.id},recipient_id.eq.${conv.id}),and(sender_id.eq.${conv.id},recipient_id.eq.${employee.id})`);
    const { data } = await query;
    setMessages(((data as Msg[]) ?? []).reverse());
    // Mark this conversation's chat notifications read.
    const link = conv.kind === "dm" ? `/chat?dm=${conv.id}` : `/chat?channel=${conv.id}`;
    await supabase.from("hq_notifications").update({ read_at: new Date().toISOString() })
      .eq("recipient_id", employee.id).is("read_at", null).eq("link", link);
    // And the read MARK, which is what the full Chat page's bold/dot reads —
    // the two ledgers must settle together whichever pane did the reading.
    void supabase.from("hq_chat_reads").upsert(
      { employee_id: employee.id, thread_key: `${conv.kind}:${conv.id}`, last_read_at: new Date().toISOString() },
      { onConflict: "employee_id,thread_key" });
    refreshUnread(); void refreshUnreadMap();
  }

  // Realtime for the active conversation.
  useEffect(() => {
    if (!active || !employee) return;
    const ch = supabase.channel(`chatdock-${active.kind}-${active.id}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "hq_chat_messages" }, (payload) => {
        const m = payload.new as Msg;
        if (m.is_direct_message && (m.sender_id === employee.id || m.recipient_id === employee.id)) {
          const other = m.sender_id === employee.id ? m.recipient_id : m.sender_id;
          if (other) setRecents(prev => bumpRecent(prev, other));
        }
        const mine = active.kind === "channel"
          ? m.channel_id === active.id
          : (m.sender_id === employee.id && m.recipient_id === active.id) || (m.sender_id === active.id && m.recipient_id === employee.id);
        if (mine) setMessages(prev => prev.some(x => x.id === m.id) ? prev : [...prev, m]);
      }).subscribe();
    return () => { void supabase.removeChannel(ch); };
  }, [active?.kind, active?.id, employee?.id]);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }); }, [messages.length, active?.id]);

  async function send() {
    const content = draft.trim();
    if ((!content && !pendingFile) || !active || !employee) return;
    setDraft("");
    setSendErr(null);
    // Upload the pasted screenshot first so the message row carries its path —
    // the same bucket, path scheme and limit as the full Chat page.
    let attach: Record<string, unknown> = {};
    const file = pendingFile;
    if (file) {
      const safeName = file.name.replace(/[^\w.\-]+/g, "_");
      const path = `${employee.id}/${Date.now()}-${safeName}`;
      const { error: upErr } = await supabase.storage
        .from("chat-attachments")
        .upload(path, file, { contentType: file.type || undefined, upsert: false });
      if (upErr) { setDraft(content); setSendErr("Upload failed: " + upErr.message); return; }
      attach = { attachment_path: path, attachment_name: file.name, attachment_type: file.type || null, attachment_size: file.size };
      setPendingFile(null);
    }
    const row: Record<string, unknown> = { sender_id: employee.id, content, is_direct_message: active.kind === "dm", ...attach };
    if (active.kind === "channel") row.channel_id = active.id; else row.recipient_id = active.id;
    let { data, error } = await supabase.from("hq_chat_messages").insert(row).select("*").single();
    // Group posting is membership-only (HQ-CHAT-003); leadership can read a
    // group it never joined. Whoever may manage the roster may join it, so a
    // refused post tries that once — same recovery as the full Chat page.
    if (error && active.kind === "channel" && /row-level security/i.test(error.message ?? "")) {
      const { data: joined } = await supabase.rpc("hq_join_group_chat", { p_channel: active.id });
      if (joined === true) {
        ({ data, error } = await supabase.from("hq_chat_messages").insert(row).select("*").single());
      }
    }
    if (error) {
      // A swallowed failure looked like a sent message that never arrived.
      setDraft(content);
      setSendErr(/row-level security/i.test(error.message ?? "")
        ? "You're not a member of this group, so it won't accept your message. Ask a group lead to add you."
        : (error.message || "Could not send. Check your connection."));
      return;
    }
    if (data) setMessages(prev => prev.some(x => x.id === (data as Msg).id) ? prev : [...prev, data as Msg]);
    if (active.kind === "dm") setRecents(prev => bumpRecent(prev, active.id));
  }

  // Dragging by the header.
  function onDragStart(e: React.MouseEvent) {
    drag.current = { dx: e.clientX - pos.x, dy: e.clientY - pos.y };
    const move = (ev: MouseEvent) => { if (drag.current) setPos(clampPos(ev.clientX - drag.current.dx, ev.clientY - drag.current.dy)); };
    const up = () => { drag.current = null; window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); setPos(p => { localStorage.setItem("chatdock_pos", JSON.stringify(p)); return p; }); };
    window.addEventListener("mousemove", move); window.addEventListener("mouseup", up);
  }

  if (!open) return null;
  const personName = (p?: Person | null) => p ? (`${p.first_name ?? ""} ${p.last_name ?? ""}`.trim() || p.email || "Someone") : "Someone";
  const filteredPeople = sortByRecent(people, p => p.id, recents).filter(p => personName(p).toLowerCase().includes(q.toLowerCase()));
  // Resolve a title even when opened from a notification (name may be blank).
  const activeTitle = active ? (active.name || (active.kind === "dm"
    ? personName(peopleMap[active.id])
    : (() => { const c = channels.find(x => x.id === active.id); return c ? ((c.is_group ? "" : "# ") + (c.name || "Group chat")) : "Chat"; })())) : "";
  const chBadge = (id: string) => unreadByConv[`channel:${id}`] || 0;
  const dmBadge = (id: string) => unreadByConv[`dm:${id}`] || 0;
  const unreadConvs = Object.entries(unreadByConv).filter(([, n]) => n > 0).map(([key, n]) => {
    const i = key.indexOf(":"); const kind = key.slice(0, i) as "dm" | "channel"; const id = key.slice(i + 1);
    const name = kind === "dm" ? personName(peopleMap[id])
      : (() => { const c = channels.find(x => x.id === id); return c ? ((c.is_group ? "" : "# ") + (c.name || "Group chat")) : "Chat"; })();
    return { kind, id, name, count: n };
  }).sort((a, b) => b.count - a.count);
  const Badge = ({ n }: { n: number }) => <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-bad px-1 text-[10px] font-bold text-white">{n}</span>;

  return (
    <div
      className="fixed z-50 flex flex-col overflow-hidden rounded-2xl border border-line bg-white shadow-2xl"
      style={{ left: pos.x, top: pos.y, width: Math.min(DOCK_W, window.innerWidth - 16), height: Math.min(DOCK_H, window.innerHeight - 16) }}
    >
      {/* Header (drag handle) */}
      <div onMouseDown={onDragStart} className="flex cursor-move items-center gap-2 border-b border-line bg-teal-dark px-3 py-2 text-white">
        {active && <button onMouseDown={e => e.stopPropagation()} onClick={() => setActive(null)} className="rounded p-0.5 hover:bg-white/20" title="Back"><ChevronLeft size={18} /></button>}
        <span className="flex-1 truncate text-sm font-semibold">{active ? activeTitle : "Chats"}</span>
        <button onMouseDown={e => e.stopPropagation()} onClick={() => setOpen(false)} className="rounded p-0.5 hover:bg-white/20" title="Close"><X size={18} /></button>
      </div>

      {!active ? (
        // Conversation list
        <div className="flex-1 overflow-y-auto">
          <div className="sticky top-0 bg-white p-2">
            <div className="flex items-center gap-2 rounded-lg border border-line px-2"><Search size={14} className="text-gray2" /><input value={q} onChange={e => setQ(e.target.value)} placeholder="Search people…" className="w-full py-1.5 text-sm outline-none" /></div>
          </div>
          {!q && unreadConvs.length > 0 && (
            <>
              <div className="px-3 pt-1 text-xs font-semibold uppercase tracking-wider text-teal-dark">Unread</div>
              {unreadConvs.map(u => (
                <button key={u.kind + u.id} onClick={() => setActive({ kind: u.kind, id: u.id, name: u.name })} className="flex w-full items-center gap-2 bg-teal-mint/15 px-3 py-1.5 text-left text-sm hover:bg-offwhite">
                  {u.kind === "channel" ? <Users size={15} className="shrink-0 text-gray2" /> : <EmployeeAvatar path={peopleMap[u.id]?.profile_picture_path ?? null} firstName={peopleMap[u.id]?.first_name ?? null} lastName={peopleMap[u.id]?.last_name ?? null} email={peopleMap[u.id]?.email ?? null} size={26} />}
                  <span className="flex-1 truncate font-semibold">{u.name}</span>
                  <Badge n={u.count} />
                </button>
              ))}
            </>
          )}
          {channels.length > 0 && <div className="px-3 pt-2 text-xs font-semibold uppercase tracking-wider text-gray2">Channels</div>}
          {channels.map(c => (
            <button key={c.id} onClick={() => setActive({ kind: "channel", id: c.id, name: (c.is_group ? "" : "# ") + (c.name || "Group chat") })} className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-offwhite">
              {c.is_group ? <Users size={15} className="shrink-0 text-gray2" /> : <span className="text-gray2">#</span>}
              <span className={`flex-1 truncate ${chBadge(c.id) ? "font-semibold" : ""}`}>{c.name || "Group chat"}</span>
              {chBadge(c.id) > 0 && <Badge n={chBadge(c.id)} />}
            </button>
          ))}
          <div className="px-3 pt-2 text-xs font-semibold uppercase tracking-wider text-gray2">Direct messages</div>
          {filteredPeople.map(p => (
            <button key={p.id} onClick={() => setActive({ kind: "dm", id: p.id, name: personName(p) })} className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-offwhite">
              <EmployeeAvatar path={p.profile_picture_path} firstName={p.first_name} lastName={p.last_name} email={p.email} size={26} />
              <span className={`flex-1 truncate ${dmBadge(p.id) ? "font-semibold" : ""}`}>{personName(p)}</span>
              {dmBadge(p.id) > 0 && <Badge n={dmBadge(p.id)} />}
            </button>
          ))}
          {filteredPeople.length === 0 && <div className="px-3 py-4 text-center text-sm text-gray2">No one found.</div>}
        </div>
      ) : (
        // Active conversation
        <>
          <div ref={scrollRef} className="flex-1 space-y-2 overflow-y-auto bg-offwhite/40 p-3">
            {messages.length === 0 && <div className="py-6 text-center text-xs text-gray2">No messages yet — say hi.</div>}
            {messages.map((m, i) => {
              const mine = m.sender_id === employee?.id;
              const divider = needsDivider(messages[i - 1]?.created_at, m.created_at);
              return (
                <div key={m.id}>
                  {divider && (
                    <div className="my-2 flex items-center gap-2">
                      <div className="h-px flex-1 bg-line" />
                      <span className="text-[10px] font-medium text-gray2">{dayDividerLabel(m.created_at)}</span>
                      <div className="h-px flex-1 bg-line" />
                    </div>
                  )}
                  <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] rounded-2xl px-3 py-1.5 text-sm ${mine ? "bg-teal-dark text-white" : "bg-white text-charcoal border border-line"}`}>
                      {!mine && active.kind === "channel" && <div className="text-[11px] font-semibold text-teal-dark">{personName(peopleMap[m.sender_id])}</div>}
                      {m.attachment_path && <ChatAttachment msg={m} isMe={mine} />}
                      {m.content && <div className="whitespace-pre-wrap break-words">{m.content}</div>}
                      <div className={`mt-0.5 text-[10px] ${mine ? "text-white/70" : "text-gray2"}`}>{fmtTime(m.created_at)}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="relative flex items-center gap-2 border-t border-line p-2">
            {/* A plain <input> can never hold a newline, so Shift+Enter had nowhere to put one —
                every Enter (shifted or not) ended the field, and Enter-without-Shift sends,
                which is what chopped a multi-line report into one message per line. A single-row
                textarea gives Shift+Enter a real newline to insert while keeping the same
                one-row footprint; it scrolls internally rather than growing (that's PR #2's job). */}
            {sendErr && (
              <div className="absolute -top-8 left-2 right-2 flex items-center justify-between gap-2 rounded-lg bg-bad-bg px-2 py-1 text-[11px] text-bad">
                <span className="truncate">{sendErr}</span>
                <button onClick={() => setSendErr(null)} className="shrink-0 hover:underline" aria-label="Dismiss error">dismiss</button>
              </div>
            )}
            {pendingFile && !sendErr && (
              <div className="absolute -top-8 left-2 right-2 flex items-center justify-between gap-2 rounded-lg bg-teal-mint/60 px-2 py-1 text-[11px] text-teal-dark">
                <span className="truncate">📎 {pendingFile.name} ({formatBytes(pendingFile.size)})</span>
                <button onClick={() => setPendingFile(null)} className="shrink-0 hover:underline" aria-label="Remove attachment">remove</button>
              </div>
            )}
            <textarea aria-label="Message" rows={1} value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(); } }} onPaste={e => {
              // A pasted screenshot becomes the pending attachment; text
              // pastes stay with the browser. Same 25 MB rule as the Chat page.
              const f = pastedImage(e);
              if (!f) return;
              e.preventDefault();
              if (f.size > MAX_ATTACH_BYTES) { setSendErr("That image is too large (max 25 MB)."); return; }
              setSendErr(null);
              setPendingFile(f);
            }} placeholder="Message…" className="flex-1 resize-none rounded-lg border border-line px-3 py-2 text-sm outline-none focus:border-teal-dark" />
            <button onClick={() => void send()} disabled={!draft.trim() && !pendingFile} className="rounded-lg bg-teal-dark p-2 text-white disabled:opacity-40" title="Send"><Send size={16} /></button>
          </div>
        </>
      )}
    </div>
  );
}
